import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Smile, Frown, Check, Loader2, ArrowRight, MapPin } from 'lucide-react';
import './WashroomFeedback.css';

const ISSUE_OPTIONS = [
  'Floor/Toilet dirty',
  'Bad odour',
  'No water',
  'No soap',
  'No tissue',
  'Dustbin full',
  'Maintenance required',
  'Other'
];

export default function WashroomFeedback() {
  const [searchParams] = useSearchParams();

  // Extract query variables
  const businessId = searchParams.get('id') ? parseInt(searchParams.get('id'), 10) : null;
  const outletParam = searchParams.get('outlet');
  const outletName = outletParam || 'Our Outlet';

  // Parse Logo URL
  const logoParam = searchParams.get('logo');
  let logoUrl = null;
  if (logoParam) {
    logoUrl = logoParam.startsWith('http') ? logoParam : `https://${logoParam}`;
  }

  // Parse Source & Table Number
  const source = searchParams.get('source') || 'QR_WASHROOM';
  const tableNumber = searchParams.get('table_number') || searchParams.get('table') || null;

  // State Hooks
  const [condition, setCondition] = useState(null); // 'satisfactory' | 'needs-attention'
  const [selectedIssues, setSelectedIssues] = useState([]);
  const [customIssue, setCustomIssue] = useState('');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Handle condition select
  const handleConditionChange = (value) => {
    setCondition(value);
    setError('');
    // Clear issue selections if Satisfactory is selected
    if (value === 'satisfactory') {
      setSelectedIssues([]);
      setCustomIssue('');
    }
  };

  // Toggle issue chips selection
  const toggleIssue = (issue) => {
    setError('');
    if (selectedIssues.includes(issue)) {
      setSelectedIssues(selectedIssues.filter((i) => i !== issue));
      if (issue === 'Other') {
        setCustomIssue('');
      }
    } else {
      setSelectedIssues([...selectedIssues, issue]);
    }
  };

  // Submit Feedback Handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validations
    if (!businessId) {
      setError('Business ID is missing or invalid. Feedback cannot be submitted.');
      return;
    }
    if (!condition) {
      setError('Please select how the washroom condition was.');
      return;
    }
    if (condition === 'needs-attention' && selectedIssues.length === 0) {
      setError('Please select at least one issue that needs attention.');
      return;
    }
    if (condition === 'needs-attention' && selectedIssues.includes('Other') && !customIssue.trim()) {
      setError('Please specify the issue in the text field.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    const ratingValue = condition === 'satisfactory' ? '5 Star' : '1 Star';
    const conditionLabel = condition === 'satisfactory' ? 'Satisfactory' : 'Needs Attention';

    // Populate "Other" custom text if present
    let finalIssues = [...selectedIssues];
    if (selectedIssues.includes('Other')) {
      finalIssues = finalIssues.map(issue => 
        issue === 'Other' && customIssue.trim() 
          ? `Other: ${customIssue.trim()}` 
          : issue
      );
    }

    // Build formatted summary text
    let textSummary = `Washroom Condition: ${conditionLabel}`;
    if (condition === 'needs-attention' && finalIssues.length > 0) {
      textSummary += `\nIssues: ${finalIssues.join(', ')}`;
    }
    if (comment.trim()) {
      textSummary += `\nComments: ${comment.trim()}`;
    }

    // Build metadata payload
    const metadata = {
      washroom_condition: conditionLabel
    };
    if (condition === 'needs-attention') {
      metadata.issues = finalIssues;
    }
    if (outletParam) {
      metadata.outlet = outletParam;
    }
    if (tableNumber) {
      metadata.table_number = tableNumber;
    }

    const payload = {
      business_id: businessId,
      rating: ratingValue,
      food_rating: null,
      service_rating: null,
      ambience_rating: null,
      text: textSummary,
      reviewer_name: null,
      reviewer_contact: null,
      source: source,
      metadata: metadata
    };

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.staging.waitwhiz.in';
      const response = await fetch(`${apiUrl}/api/v1/reviews/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const responseData = await response.json();

      if (response.ok) {
        setSubmitted(true);
      } else {
        throw new Error(responseData?.message || responseData?.detail || 'Failed to submit review');
      }
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setCondition(null);
    setSelectedIssues([]);
    setCustomIssue('');
    setComment('');
    setSubmitted(false);
    setError('');
  };

  return (
    <div className="wf-page-container">
      <div className="wf-feedback-card">
        
        {/* Decorative Top Accent Bar */}
        <div className="wf-feedback-card__accent-bar" />

        {/* Brand Header */}
        <div className="wf-feedback-card__header">
          {logoUrl && (
            <div className="wf-feedback-card__logo-frame">
              <img 
                src={logoUrl} 
                alt="Brand Logo" 
                className="wf-feedback-card__logo" 
              />
            </div>
          )}
          <span className="wf-feedback-card__category-label">
            Cleanliness & Maintenance
          </span>
        </div>

        {/* Content Section */}
        <div className="wf-feedback-card__content">
          {!submitted ? (
            <form onSubmit={handleSubmit} className="wf-feedback-card__form">
              <div>
                {/* Location / Outlet Badge */}
                {outletParam && (
                  <div className="wf-location-badge">
                    <div className="wf-location-badge__icon-box">
                      <MapPin className="wf-location-badge__icon" />
                    </div>
                    <div>
                      <p className="wf-location-badge__title">Selected Outlet</p>
                      <p className="wf-location-badge__name">{outletName}</p>
                    </div>
                  </div>
                )}

                {/* Sentiment / Condition Selection */}
                <div className="wf-form-group">
                  <label className="wf-form-group__label">
                    How was the washroom condition? <span className="wf-form-group__required">*</span>
                  </label>
                  
                  <div className="wf-condition-selector-grid">
                    {/* Satisfactory Card Toggle */}
                    <button
                      type="button"
                      onClick={() => handleConditionChange('satisfactory')}
                      className={`wf-condition-card satisfactory ${
                        condition === 'satisfactory' ? 'wf-condition-card--active' : ''
                      }`}
                    >
                      <div className={`wf-condition-card__icon-wrap ${
                        condition === 'satisfactory' ? 'wf-condition-card__icon-wrap--active' : ''
                      }`}>
                        <Smile className="wf-condition-card__icon" />
                      </div>
                      <span className={`wf-condition-card__label ${
                        condition === 'satisfactory' ? 'wf-condition-card__label--active' : ''
                      }`}>
                        Satisfactory
                      </span>
                    </button>

                    {/* Needs Attention Card Toggle */}
                    <button
                      type="button"
                      onClick={() => handleConditionChange('needs-attention')}
                      className={`wf-condition-card attention ${
                        condition === 'needs-attention' ? 'wf-condition-card--active' : ''
                      }`}
                    >
                      <div className={`wf-condition-card__icon-wrap ${
                        condition === 'needs-attention' ? 'wf-condition-card__icon-wrap--active' : ''
                      }`}>
                        <Frown className="wf-condition-card__icon" />
                      </div>
                      <span className={`wf-condition-card__label ${
                        condition === 'needs-attention' ? 'wf-condition-card__label--active' : ''
                      }`}>
                        Needs Attention
                      </span>
                    </button>
                  </div>
                </div>

                {/* Dynamic Issues Checklist (Chips/Tags) */}
                <div 
                  className={`wf-expandable-section ${
                    condition === 'needs-attention' ? 'wf-expandable-section--visible' : ''
                  }`}
                >
                  <label className="wf-form-group__label">
                    What issue did you notice? <span className="wf-form-group__required">*</span>
                  </label>

                  <div className="wf-chips-wrapper">
                    {ISSUE_OPTIONS.map((issue) => {
                      const isSelected = selectedIssues.includes(issue);
                      return (
                        <button
                          key={issue}
                          type="button"
                          onClick={() => toggleIssue(issue)}
                          className={`wf-chip-tag ${isSelected ? 'wf-chip-active' : ''}`}
                        >
                          <span>{issue}</span>
                          {isSelected && <Check className="wf-chip-check-icon" />}
                        </button>
                      );
                    })}
                  </div>

                  {/* Inline Specify Text Field */}
                  <div 
                    className={`wf-custom-issue-wrap ${
                      selectedIssues.includes('Other') ? 'wf-custom-issue-wrap--visible' : ''
                    }`}
                  >
                    <label className="wf-custom-issue-label">
                      Please specify the issue <span className="wf-form-group__required">*</span>
                    </label>
                    <input
                      type="text"
                      value={customIssue}
                      onChange={(e) => setCustomIssue(e.target.value)}
                      placeholder="Specify issue detail..."
                      className="wf-custom-issue-input"
                      maxLength={80}
                    />
                  </div>
                </div>

                {/* Additional Comment Field */}
                <div className="wf-form-group">
                  <label className="wf-form-group__label">
                    Any additional comment? <span className="wf-form-group__optional">(optional)</span>
                  </label>
                  <textarea
                    rows={3}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Write your feedback here..."
                    className="wf-comments-textarea"
                    maxLength={200}
                  />
                </div>

                {/* Error Banner */}
                {error && (
                  <div className="wf-error-banner">
                    <div className="wf-error-dot" />
                    <span>{error}</span>
                  </div>
                )}
              </div>

              {/* Submit CTA */}
              <button
                type="submit"
                disabled={isSubmitting || !condition || (condition === 'needs-attention' && selectedIssues.length === 0)}
                className={`wf-submit-btn ${
                  !condition || (condition === 'needs-attention' && selectedIssues.length === 0) ? 'wf-submit-btn--disabled' : ''
                }`}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="wf-submit-btn__spinner" />
                    <span>Submitting Feedback...</span>
                  </>
                ) : (
                  <>
                    <span>Submit Feedback</span>
                    <ArrowRight className="wf-submit-btn__arrow" />
                  </>
                )}
              </button>
            </form>
          ) : (
            /* Success Screen View */
            <div className="wf-success-view">
              <div className="wf-success-view__icon-wrap">
                <Check className="wf-success-view__check" />
              </div>
              
              <h2 className="wf-success-view__title">
                Thank You for Your Feedback
              </h2>
              
              <p className="wf-success-view__message">
                Your response helps us maintain clean and hygienic spaces at <span className="wf-success-view__highlight">{outletName}</span>.
              </p>

              <button
                type="button"
                onClick={resetForm}
                className="wf-reset-btn"
              >
                Submit New Report
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
